import { useMemo, useState } from 'react'
import { ErroreApi } from '../../servizi/api.ts'
import { Avviso, Vuoto } from '../../componenti/Pezzi.tsx'
import { Bottone, Campo, Etichetta, Modale, Riquadro, Spunta, Tabella } from '../../componenti/base.tsx'
import * as posta from '../../servizi/posta.ts'
import { creaGruppo, eliminaGruppo, modificaGruppo, type IndirizzoPerGruppo } from '../../servizi/posta-messaggio.ts'
import '../../stile-posta-caselle.css'
import '../../stile-posta-messaggio.css'

/**
 * Gruppi della rubrica (539, come in Roundcube): un nome per piu' contatti. Nel compositore, scrivendo il nome
 * del gruppo in A, Cc o Ccn, ne arrivano gli indirizzi. Si creano, rinominano ed eliminano, e si scelgono i
 * contatti; un collega aggiunto a un gruppo entra anche nella rubrica. Li cambia chi corregge la rubrica.
 */

const testoErrore = (e: unknown) =>
  (e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')

export function GruppiRubrica({ casellaId, rubrica, gestisce, cerca, aggiorna, scrivi }: {
  casellaId: string
  rubrica: posta.Rubrica
  gestisce: boolean
  /** Il filtro della pagina: nome del gruppo o di un suo contatto. */
  cerca: string
  aggiorna: (messaggio: string) => void
  scrivi: (casellaId: string, destinatari: string) => void
}) {
  const [modifica, setModifica] = useState<posta.GruppoRubrica | 'nuovo' | null>(null)
  const [elimina, setElimina] = useState<posta.GruppoRubrica | null>(null)
  const perId = useMemo(() => new Map(rubrica.contatti.map((c) => [c.id, c])), [rubrica])
  const gruppi = rubrica.gruppi ?? []
  const q = cerca.trim().toLowerCase()
  const visibili = gruppi.filter((g) => !q || g.nome.toLowerCase().includes(q)
    || g.contatti.some((id) => { const c = perId.get(id); return !!c && (c.indirizzo.includes(q) || !!c.nome?.toLowerCase().includes(q)) }))

  if (!gruppi.length && !gestisce) return null
  return (
    <>
      <Riquadro denso titolo="Gruppi"
                azioni={gestisce && <Bottone dimensione="piccolo" onClick={() => setModifica('nuovo')}>Nuovo gruppo</Bottone>}>
        <Tabella intestazioni={['Gruppo', 'Contatti', '']}
                 vuoto={<Vuoto testo={q ? 'Nessun gruppo trovato.' : 'Nessun gruppo: «Nuovo gruppo» ne crea uno, per esempio gli uffici di un comune.'} />}>
          {visibili.map((g) => (
            <tr key={g.id}>
              <td data-etichetta="Gruppo">{g.nome}</td>
              <td data-etichetta="Contatti">
                <span className="rubrica-gruppo-membri">
                  {g.contatti.length === 0 && <span className="tenue">nessuno</span>}
                  {g.contatti.slice(0, 6).map((id) => {
                    const c = perId.get(id)
                    return c ? <Etichetta key={id}>{c.nome ?? c.indirizzo}</Etichetta> : null
                  })}
                  {g.contatti.length > 6 && <span className="tenue">e altri {g.contatti.length - 6}</span>}
                </span>
              </td>
              <td data-etichetta="" className="rubrica-azioni">
                {g.contatti.length > 0 && (
                  <Bottone dimensione="piccolo" variante="discreto"
                           onClick={() => scrivi(casellaId, posta.vociDelGruppo(rubrica, g).join(', '))}>Scrivi</Bottone>
                )}
                {gestisce && (
                  <>
                    <Bottone dimensione="piccolo" variante="discreto" onClick={() => setModifica(g)}>Modifica</Bottone>
                    <Bottone dimensione="piccolo" variante="discreto" onClick={() => setElimina(g)}>Elimina</Bottone>
                  </>
                )}
              </td>
            </tr>
          ))}
        </Tabella>
      </Riquadro>
      {modifica && (
        <ModuloGruppo casellaId={casellaId} rubrica={rubrica} gruppo={modifica === 'nuovo' ? null : modifica}
                      chiudi={() => setModifica(null)}
                      salvato={(testo) => { setModifica(null); aggiorna(testo) }} />
      )}
      {elimina && (
        <ConfermaElimina gruppo={elimina} chiudi={() => setElimina(null)}
                         eliminato={() => { setElimina(null); aggiorna(`Il gruppo «${elimina.nome}» non c’è più; i contatti restano.`) }} />
      )}
    </>
  )
}

/** Nome e contatti del gruppo: i contatti della rubrica e i colleghi, con una ricerca. */
function ModuloGruppo({ casellaId, rubrica, gruppo, chiudi, salvato }: {
  casellaId: string
  rubrica: posta.Rubrica
  gruppo: posta.GruppoRubrica | null
  chiudi: () => void
  salvato: (testo: string) => void
}) {
  const [nome, setNome] = useState(gruppo?.nome ?? '')
  const [scelti, setScelti] = useState<Set<string>>(() => new Set(gruppo?.contatti ?? []))
  const [colleghi, setColleghi] = useState<Set<string>>(() => new Set())
  const [cerca, setCerca] = useState('')
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const q = cerca.trim().toLowerCase()
  const trova = (v: { nome: string | null; indirizzo: string }) =>
    !q || v.indirizzo.includes(q) || !!v.nome?.toLowerCase().includes(q)
  const nomeErrato = /[@,;<>"]/.test(nome)

  const alterna = <T,>(insieme: Set<T>, valore: T, si: boolean) => {
    const nuovo = new Set(insieme)
    if (si) nuovo.add(valore); else nuovo.delete(valore)
    return nuovo
  }

  async function salva() {
    setInCorso(true); setErrore(null)
    const indirizzi: IndirizzoPerGruppo[] = rubrica.colleghi.filter((c) => colleghi.has(c.indirizzo))
      .map((c) => ({ indirizzo: c.indirizzo, nome: c.nome }))
    try {
      if (!gruppo) {
        await creaGruppo(casellaId, nome.trim(), [...scelti], indirizzi)
        salvato(`Il gruppo «${nome.trim()}» è nella rubrica.`)
      } else {
        const prima = new Set(gruppo.contatti)
        await modificaGruppo(gruppo.id, {
          ...(nome.trim() !== gruppo.nome ? { nome: nome.trim() } : {}),
          aggiungi: [...scelti].filter((id) => !prima.has(id)),
          togli: [...prima].filter((id) => !scelti.has(id)),
          indirizzi,
        })
        salvato(`Gruppo «${nome.trim()}» aggiornato.`)
      }
    } catch (e) {
      setErrore(testoErrore(e))
      setInCorso(false)
    }
  }

  const quanti = scelti.size + colleghi.size
  return (
    <Modale titolo={gruppo ? `Modifica «${gruppo.nome}»` : 'Nuovo gruppo'} chiudi={chiudi} azioni={(
      <>
        <Bottone onClick={chiudi}>Annulla</Bottone>
        <Bottone variante="primario" inCorso={inCorso} disabled={!nome.trim() || nomeErrato} onClick={() => void salva()}>
          Salva
        </Bottone>
      </>
    )}>
      <Campo etichetta="Nome del gruppo" valore={nome} cambia={setNome} obbligatorio maxLength={80}
             errore={nomeErrato ? 'Senza @, virgole, punti e virgola, virgolette o parentesi angolari.' : null}
             aiuto="Scritto in A, Cc o Ccn, il nome diventa gli indirizzi del gruppo." />
      <Campo tipo="search" etichetta="Cerca fra i contatti" valore={cerca} cambia={setCerca} segnaposto="nome o indirizzo" />
      <p className="piccolo tenue">{quanti === 1 ? '1 contatto scelto' : `${quanti} contatti scelti`}</p>
      <div className="rubrica-scelta-contatti" role="group" aria-label="Contatti del gruppo">
        {rubrica.contatti.filter(trova).map((c) => (
          <Spunta key={c.id} valore={scelti.has(c.id)} cambia={(si) => setScelti((s) => alterna(s, c.id, si))}
                  etichetta={<>{c.nome ?? c.indirizzo}{c.nome && <span className="tenue"> · {c.indirizzo}</span>}</>} />
        ))}
        {rubrica.colleghi.filter(trova).map((c) => (
          <Spunta key={c.indirizzo} valore={colleghi.has(c.indirizzo)}
                  cambia={(si) => setColleghi((s) => alterna(s, c.indirizzo, si))}
                  etichetta={<>{c.nome} <span className="tenue">· {c.indirizzo} · collega</span></>} />
        ))}
        {!rubrica.contatti.some(trova) && !rubrica.colleghi.some(trova) && (
          <span className="piccolo tenue">Nessun contatto trovato.</span>
        )}
      </div>
      {colleghi.size > 0 && (
        <p className="piccolo tenue">I colleghi scelti entrano anche nella rubrica di questa casella.</p>
      )}
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}

function ConfermaElimina({ gruppo, chiudi, eliminato }: {
  gruppo: posta.GruppoRubrica; chiudi: () => void; eliminato: () => void
}) {
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  return (
    <Modale titolo="Elimina il gruppo" chiudi={chiudi} fuocoIniziale="azioni" azioni={(
      <>
        <Bottone onClick={chiudi}>Annulla</Bottone>
        <Bottone variante="critico" inCorso={inCorso} onClick={() => {
          setInCorso(true)
          eliminaGruppo(gruppo.id).then(eliminato, (e) => { setErrore(testoErrore(e)); setInCorso(false) })
        }}>Elimina</Bottone>
      </>
    )}>
      <p>Il gruppo «{gruppo.nome}» non sarà più proposto nel compositore. I suoi contatti restano nella rubrica.</p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
    </Modale>
  )
}
